import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api";
import { listLevels } from "../../api/levels";
import { formatLevelLabel } from "../../utils/formatLevelLabel";
import { listCoachRosterStudents, listStudents, listCoaches, listManagerActiveStudents } from "../../api/users";
import { useCurrentUser } from "../../context/UserContext";
import { DatePicker } from "../common/DatePicker";
import { showConfirm } from '../../utils/popup';
import { formatTimeFromHhMm } from "../../utils/regionalFormat";
import { filterHhMmTimes } from "../../utils/timeOptions";
import { getCurrencySymbol } from "../../utils/currency";
import { TextInput, SelectInput } from "../common/form";
import { toISODateFromRegional } from "../../utils/date";
import { apiErrorMessage } from '../../utils/apiError';

// Generate time options with 15-minute intervals
const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            const hour = String(h).padStart(2, "0");
            const minute = String(m).padStart(2, "0");
            options.push(`${hour}:${minute}`);
        }
    }
    return options;
};

const formatTimeToAMPM = (time24) => formatTimeFromHhMm(time24);

const WalkInSessionModal = ({ role, isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const currencySymbol = getCurrencySymbol();
    const { user } = useCurrentUser();
    const [form, setForm] = useState({
        studentIds: [],
        levelId: "",
        coachId: "",
        sessionDate: "",
        startTime: "",
        endTime: "",
        sessionCharge: "",
    });

    const allTimeOptions = useMemo(() => generateTimeOptions(), []);
    const startTimeOptions = useMemo(
        () => filterHhMmTimes(allTimeOptions, { maxTime: form.endTime || null }),
        [allTimeOptions, form.endTime]
    );
    const endTimeOptions = useMemo(
        () => filterHhMmTimes(allTimeOptions, { minTime: form.startTime || null }),
        [allTimeOptions, form.startTime]
    );

    const [levels, setLevels] = useState([]);
    const [students, setStudents] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [studentSearch, setStudentSearch] = useState("");
    const [showStudentDropdown, setShowStudentDropdown] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        let alive = true;

        // Load levels, students, and coaches
        (async () => {
            try {
                // Fetch levels
                const levelsRes = await listLevels(role, { page: 0, size: 500 });
                const levelsList = levelsRes?.data?.content ?? levelsRes?.data ?? levelsRes ?? [];

                let studentsList = [];
                if (role === "COACH") {
                    studentsList = await listCoachRosterStudents();
                } else if (role === "MANAGER") {
                    studentsList = await listManagerActiveStudents();
                } else {
                    studentsList = await listStudents({ page: 0, size: 500 });
                }

                // For COACH role, use current user instead of fetching all coaches (403 error)
                let coachesList = [];
                if (role === "COACH" && user) {
                    coachesList = [user];
                } else if (role === "MANAGER") {
                    const res = await API.get("/manager/users", { params: { role: "COACH", page: 0, size: 500 } });
                    const data = res?.data;
                    coachesList = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
                } else {
                    // For ADMIN, fetch all coaches
                    coachesList = await listCoaches({ page: 0, size: 500 });
                }

                if (!alive) return;

                setLevels(levelsList);
                setStudents(studentsList);
                setCoaches(coachesList);

                // Auto-select coach if logged in as COACH
                if (role === "COACH" && user?.id) {
                    setForm(prev => ({ ...prev, coachId: String(user.id) }));
                }
            } catch (err) {
                console.error("Error loading data:", err);
                if (alive) {
                    setStudents([]);
                    setLevels([]);
                    setCoaches([]);
                    setError(t('common.optionsLoadFailed'));
                }
            }
        })();

        return () => { alive = false; };
    }, [isOpen, role, user, t]);

    const getFullName = (user) => {
        const parts = [user.firstName, user.middleName, user.lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : user.username || t('sessions.walkInModal.userFallback', { id: user.id || user.userId });
    };

    const getSelectedStudents = () => {
        return form.studentIds.map(id => students.find(s => (s.id || s.userId) === id)).filter(Boolean);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (value) => {
        setForm((prev) => ({ ...prev, sessionDate: value }));
    };

    const handleStudentSelect = (studentId) => {
        if (!form.studentIds.includes(studentId)) {
            setForm((prev) => ({ ...prev, studentIds: [...prev.studentIds, studentId] }));
        }
        setStudentSearch("");
        setShowStudentDropdown(false);
    };

    const handleStudentRemove = (studentId) => {
        setForm((prev) => ({
            ...prev,
            studentIds: prev.studentIds.filter(id => id !== studentId)
        }));
    };

    const filteredStudents = students.filter(s => {
        if (!studentSearch.trim()) return true;
        const searchLower = studentSearch.toLowerCase();
        const studentId = s.id || s.userId;
        const fullName = getFullName(s).toLowerCase();
        return fullName.includes(searchLower) || String(studentId).includes(searchLower);
    }).filter(s => !form.studentIds.includes(s.id || s.userId));

    const calculateDuration = (startTime, endTime) => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        return (endMinutes - startMinutes) / 60; // duration in hours
    };

    const formatDuration = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        if (m === 0) return t('sessions.form.hour', { count: h });
        return `${t('sessions.form.hour', { count: h })} ${t('sessions.form.minute', { count: m })}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Validation
        if (form.studentIds.length === 0 || !form.levelId || !form.coachId || !form.sessionDate || !form.startTime || !form.endTime || !form.sessionCharge) {
            setError(t('sessions.walkInModal.fillRequired'));
            return;
        }

        if (form.endTime <= form.startTime) {
            setError(t('sessions.walkInModal.endAfterStart'));
            return;
        }

        const duration = calculateDuration(form.startTime, form.endTime);
        if (duration > 3) {
            const confirmed = await showConfirm(
                `⚠️ ${t('sessions.walkInModal.longSessionTitle')}\n\n` +
                `${t('sessions.walkInModal.sessionDateLabel')} ${form.sessionDate}\n` +
                `${t('sessions.walkInModal.startTimeLabel')} ${formatTimeToAMPM(form.startTime)}\n` +
                `${t('sessions.walkInModal.endTimeLabel')} ${formatTimeToAMPM(form.endTime)}\n` +
                `${t('sessions.walkInModal.durationLabel')} ${formatDuration(duration)}\n\n` +
                t('sessions.walkInModal.longSessionProceed')
            );
            if (!confirmed) {
                return;
            }
        }

        setLoading(true);

        try {
            const isoDate = toISODateFromRegional(form.sessionDate);
            if (!isoDate) {
                setLoading(false);
                return;
            }

            // Combine date + time to ISO format (YYYY-MM-DDTHH:mm)
            const startTimeISO = `${isoDate}T${form.startTime}`;
            const endTimeISO = `${isoDate}T${form.endTime}`;

            const payload = {
                studentIds: form.studentIds.map(Number),
                levelId: Number(form.levelId),
                coachId: form.coachId ? Number(form.coachId) : null,
                startTime: startTimeISO,
                endTime: endTimeISO,
                sessionCharge: Number(form.sessionCharge),
            };
            const basePath = role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach";
            const res = await API.post(`${basePath}/sessions/walk-in`, payload);

            if (onSuccess) {
                onSuccess(res.data);
            }

            // Reset form
            setForm({
                studentIds: [],
                levelId: "",
                coachId: "",
                sessionDate: "",
                startTime: "",
                endTime: "",
                sessionCharge: "",
            });

            onClose();
        } catch (err) {
            console.error("Error creating walk-in session:", err);
            setError(apiErrorMessage(err, t('sessions.walkInModal.createFailed')));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header with gradient and close button */}
                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-5 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-white">🚶 {t('sessions.walkInModal.title')}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                        disabled={loading}
                    >
                        ✕
                    </button>
                </div>

                <div className="p-5">
                    {error && (
                        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
                            {error}
                        </div>
                    )}

                    {/* Info Box */}
                    <div className="mb-4 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg">
                        <p className="text-xs text-teal-800">
                            <strong>{t('sessions.walkInModal.info')}</strong> {t('sessions.walkInModal.infoBody')}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        {/* Students Section */}
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-3 rounded-lg border border-teal-100">
                            <label className="block text-xs font-semibold text-teal-900 mb-2">
                                👥 {t('sessions.walkInModal.students')} <span className="text-red-500">*</span>
                            </label>

                            {/* Selected Students Display */}
                            {form.studentIds.length > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                    {getSelectedStudents().map((student) => {
                                        const studentId = student.id || student.userId;
                                        return (
                                            <div
                                                key={studentId}
                                                className="flex items-center bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full text-xs"
                                            >
                                                <span>{getFullName(student)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleStudentRemove(studentId)}
                                                    className="ml-1.5 text-teal-600 hover:text-teal-900 font-bold"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Search Input */}
                            <div className="relative">
                                <TextInput
                                    type="text"
                                    placeholder={t('sessions.walkInModal.searchStudents')}
                                    value={studentSearch}
                                    onChange={(e) => setStudentSearch(e.target.value)}
                                    onFocus={() => setShowStudentDropdown(true)}
                                    className="text-xs"
                                />

                                {/* Dropdown */}
                                {showStudentDropdown && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowStudentDropdown(false)}
                                        />
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-teal-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {filteredStudents.length === 0 ? (
                                                <div className="p-2.5 text-gray-500 text-xs">
                                                    {students.length === 0 && error === t('common.optionsLoadFailed')
                                                        ? t('common.optionsLoadFailed')
                                                        : studentSearch.trim()
                                                            ? t('sessions.walkInModal.noStudentsFound')
                                                            : t('sessions.walkInModal.allStudentsSelected')}
                                                </div>
                                            ) : (
                                                filteredStudents.map((s) => {
                                                    const studentId = s.id || s.userId;
                                                    return (
                                                        <div
                                                            key={studentId}
                                                            onClick={() => handleStudentSelect(studentId)}
                                                            className="p-2.5 hover:bg-teal-50 cursor-pointer border-b last:border-b-0"
                                                        >
                                                            <div className="font-medium text-xs">{getFullName(s)}</div>
                                                            <div className="text-xs text-gray-500">{t('sessions.walkInModal.idLabel', { id: studentId })}</div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {form.studentIds.length === 0 && (
                                <p className="mt-1.5 text-xs text-teal-600">
                                    {t('sessions.walkInModal.searchHint')}
                                </p>
                            )}
                        </div>

                        {/* Session Details Section */}
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-100 space-y-2.5">
                            <h3 className="text-xs font-semibold text-indigo-900 mb-2">📋 {t('sessions.walkInModal.sessionDetails')}</h3>

                            <SelectInput
                                label={t('sessions.level')}
                                name="levelId"
                                value={form.levelId}
                                onChange={handleChange}
                                className="text-xs"
                                required
                            >
                                <option value="">{t('sessions.walkInModal.selectLevel')}</option>
                                {levels.map((l) => (
                                    <option key={l.levelId || l.id} value={l.levelId || l.id}>
                                        {formatLevelLabel(l)}
                                    </option>
                                ))}
                            </SelectInput>

                            <SelectInput
                                label={t('sessions.walkInModal.sessionCoach')}
                                name="coachId"
                                value={form.coachId}
                                onChange={handleChange}
                                className="text-xs"
                                disabled={role === "COACH"}
                                required
                                note={role === "COACH" ? t('sessions.walkInModal.coachAutoNote') : undefined}
                            >
                                <option value="">{t('sessions.walkInModal.selectCoach')}</option>
                                {coaches.map((c) => (
                                    <option key={c.id || c.userId} value={c.id || c.userId}>
                                        {getFullName(c)} (#{c.id || c.userId})
                                    </option>
                                ))}
                            </SelectInput>
                        </div>

                        {/* Timing Section */}
                        <div className="bg-gradient-to-r from-blue-50 to-sky-50 p-3 rounded-lg border border-blue-100 space-y-2.5">
                            <h3 className="text-xs font-semibold text-blue-900 mb-2">⏰ {t('sessions.walkInModal.timing')}</h3>

                            <div>
                                <label className="block text-xs font-medium text-blue-900 mb-1">
                                    {t('sessions.walkInModal.sessionDate')} <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.sessionDate}
                                    onChange={handleDateChange}
                                    className="w-full text-xs"
                                />
                            </div>

                            {/* Start Time & End Time */}
                            <div className="grid grid-cols-2 gap-2.5">
                                <SelectInput
                                    label={t('sessions.walkInModal.startTime')}
                                    name="startTime"
                                    value={form.startTime}
                                    onChange={handleChange}
                                    className="text-xs"
                                    required
                                >
                                    <option value="">{t('sessions.walkInModal.selectTime')}</option>
                                    {startTimeOptions.map((time) => (
                                        <option key={time} value={time}>
                                            {formatTimeToAMPM(time)}
                                        </option>
                                    ))}
                                </SelectInput>

                                <SelectInput
                                    label={t('sessions.walkInModal.endTime')}
                                    name="endTime"
                                    value={form.endTime}
                                    onChange={handleChange}
                                    className="text-xs"
                                    required
                                >
                                    <option value="">{t('sessions.walkInModal.selectTime')}</option>
                                    {endTimeOptions.map((time) => (
                                        <option key={time} value={time}>
                                            {formatTimeToAMPM(time)}
                                        </option>
                                    ))}
                                </SelectInput>
                            </div>
                        </div>

                        {/* Payment Section */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-100">
                            <TextInput
                                label={t('sessions.walkInModal.sessionCharge', { symbol: currencySymbol })}
                                type="number"
                                name="sessionCharge"
                                value={form.sessionCharge}
                                onChange={handleChange}
                                className="text-xs"
                                placeholder={t('sessions.walkInModal.enterAmount')}
                                min="0"
                                step="0.01"
                                required
                                note={t('sessions.walkInModal.chargeNote')}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end space-x-2.5 pt-3 border-t">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-xs font-medium disabled:opacity-60"
                                disabled={loading}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 text-xs font-medium disabled:opacity-60"
                                disabled={loading}
                            >
                                {loading ? t('sessions.walkInModal.creating') : t('sessions.walkInModal.create')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default WalkInSessionModal;
